import { Button, makeStyles } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import React from 'react';

import { ReactComponent as TimelineFAQSvg } from '../../../assets/timeline_search_faq.svg';
import { ReactComponent as TimelinePostSvg } from '../../../assets/timeline_search_post.svg';
import { PostType, PublishStatus, QuestionStatus } from '../../../graphql/gen/types';
import { bottomBorderStyle, colorPalette, spacings, TextSizesEnum } from '../../../theme';
import { LinesEllipsis } from '../../shared/primitives/LinesEllipsis';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { PlexName } from '../common/PlexName';
import { useSelectedPlexPost } from '../details/post/useSelectedPlexPost';
import { useSelectedPlexQuestion } from '../details/question/useSelectedQuestion';
import { logAnalytics } from '../shared/logAnalytics';
import { TimelineSearchEventSvg } from './TimelineSearchEventSvg';
import { SearchResultOption } from './useSearch';

interface SearchItemData {
  icon: React.ReactNode;
  title: string;
  body: string;
  plexName: string;
  publishStatus: PublishStatus | QuestionStatus;
  publishedDate: Date;
  viewOnClick: () => void;
}

interface Props {
  searchResult: SearchResultOption;
  onSelect: (searchResult: SearchResultOption, selected: boolean) => void;
  selected: boolean;
  plexName: string;
  plexColor: string | null | undefined;
}

export const SearchResultItemSlate = React.memo(
  ({ searchResult, plexName, plexColor, selected, onSelect }: Props): React.ReactElement | null => {
    const classes = useStyles();
    const [, setSelectedPlexPost] = useSelectedPlexPost();
    const [, setSelectedPlexQuestion] = useSelectedPlexQuestion();
    if (!searchResult.body) {
      return null;
    }
    const icon = <CheckBoxOutlineBlankIcon className={classes.checkIcon} fontSize="small" />;
    const checkedIcon = <CheckBoxIcon className={classes.checkIcon} fontSize="small" />;

    const getItemData = (): SearchItemData | null => {
      switch (searchResult.body?.__typename) {
        case 'SearchPostBody': {
          return {
            icon: searchResult.body?.postType === PostType.Event ? <TimelineSearchEventSvg /> : <TimelinePostSvg />,
            title: searchResult.body.title || '',
            body: searchResult.body.body || '',
            plexName,
            // TODO this is wrong, we should always have a value here
            publishStatus: searchResult.body.publishStatus || PublishStatus.Internal,
            publishedDate: searchResult.body.updatedAt || '',
            viewOnClick: (): void => {
              logAnalytics({ name: 'search result clicked' });
              setSelectedPlexPost({ permalink: searchResult.body.permalink, commentsVisible: false });
            },
          };
        }
        case 'SearchQuestionBody': {
          return {
            icon: <TimelineFAQSvg />,
            title: searchResult.body.text || '',
            body: searchResult.body.acceptedAnswerBody || '',
            plexName,
            publishStatus: searchResult.body.questionStatus || QuestionStatus.Internal,
            publishedDate: searchResult.body.updatedAt || '',
            viewOnClick: (): void => {
              logAnalytics({ name: 'search result clicked' });
              setSelectedPlexQuestion({ permalink: searchResult.body.permalink, commentsVisible: false });
            },
          };
        }
        default: {
          return null;
        }
      }
    };

    const itemData = getItemData();
    if (!itemData) {
      return null;
    }

    return (
      <Button
        className={classes.postButtonContainer}
        onClick={(): void => {
          onSelect(searchResult, !selected);
        }}
      >
        {selected ? checkedIcon : icon}
        <div className={classes.postContent}>
          <div className={classes.bubbleRow}>
            <PlexName name={plexName} color={plexColor} className={classes.plexBubble} />
          </div>
          <PlexTypography className={classes.plexTitle} text={itemData.title} size={TextSizesEnum.Normal} />
          <PlexTypography className={classes.plexBody} size={TextSizesEnum.Small}>
            <LinesEllipsis text={itemData.body} maxLine={2} />
          </PlexTypography>
        </div>
      </Button>
    );
  }
);

const useStyles = makeStyles((theme) => ({
  checkIcon: {
    marginRight: 12,
    color: colorPalette.primaryColorGreen.primary,
    width: 24,
    height: 24,
  },
  postButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    borderRadius: 0,
    padding: spacings.normal,
    ...bottomBorderStyle,
  },
  postContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  bubbleRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  plexBubble: {
    marginRight: spacings.small,
  },
  plexTitle: {
    color: '#464663',
  },
  plexBody: {
    color: colorPalette.purpleGray._500,
  },
}));
